import React, { useCallback, useContext, useEffect, useState } from "react";
import { Flex } from "rebass/styled-components";

import { layout } from "../../constants/constants";
import GlobalTool from "../../global-toolbar/global-tool/global-tool";
import ShIcon from "../../shared/components/sh-icon/sh-icon";
import ShLink from "../../shared/components/sh-link/sh-link";
import { Media } from "../../shared/contexts/media/MediaContext";
import { UiStateContext } from "../../shared/contexts/ui-state/UiStateContext";
import useFeatureToggle from "../../shared/hooks/use-feature-toggle/useFeatureToggle";
import { FeatureToggle, GlobalNavContent, IconPack, GlobalToolbarContent } from "../../shared/models/app-data-model";
import MegaMenu from "../mega-menu/mega-menu";
import "../shared/navigation-shared.scss";
import "./mobile-nav-overlay.scss";

export type MobileNavOverlayProps = {
  content: GlobalNavContent;
  iconPack: IconPack[];
  featureToggles: FeatureToggle[];
  globalToolbarContent?: GlobalToolbarContent;
};

const MobileNavOverlay: React.FC<MobileNavOverlayProps> = ({
  content,
  iconPack,
  featureToggles,
  globalToolbarContent
}: MobileNavOverlayProps) => {
  const uiStateContext = useContext(UiStateContext);
  const globalToolbarEnabled = useFeatureToggle("global-toolbar", featureToggles);
  const megaMenuEnabled = useFeatureToggle("Corewell-health-mega-menu", featureToggles);
  const [selectedNavIndex, setSelectedNavIndex] = useState<number>();
  const [selectedNavIndexTablet, setSelectedNavIndexTablet] = useState<number>(
    content?.primary_nav?.findIndex((el) =>
      el.primary_nav_item?.mega_menu?.find((el) => el.column_1_content || el.column_2_content || el.column_3_content || el.column_4_content)
    )
  );

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "instant"
    });
  }, []);

  const handleCloseButton = useCallback((): void => {
    uiStateContext?.setMenuOpen?.(false);
    uiStateContext?.setMegaMenuOpen?.(false);

    setTimeout(() => {
      window.scrollTo({
        top: uiStateContext.scrollPosition,
        behavior: "smooth"
      });
    }, 1);
  }, [uiStateContext.setMenuOpen, uiStateContext.scrollPosition]);

  const handleMegaButtonClick = (index: number) => {
    setSelectedNavIndex(index);
    setSelectedNavIndexTablet(index);
    uiStateContext?.setMegaMenuOpen?.(true);
  };

  const noMegaMenus = !content?.primary_nav?.some((el) =>
    el.primary_nav_item?.mega_menu?.find((el) => el.column_1_content || el.column_2_content || el.column_3_content || el.column_4_content)
  );

  const topSectionCloseIcon = (
    <Flex alignItems="center" flexDirection="row-reverse" width={layout.pageWidth} padding="12px 10px 32px 0px">
      <button className="unstyled-btn close-btn" data-testid="close-btn" onClick={() => handleCloseButton()}>
        <ShIcon iconPack={iconPack} className="icon close-icon" iconName="close" />
      </button>
    </Flex>
  );

  const navActionLinks = (
    <div className="nav-action-links">
      {globalToolbarContent?.toolbar_left_content?.map((tool, index) => {
        return <GlobalTool key={index} content={tool} iconPack={iconPack} featureToggles={featureToggles} />;
      })}
      {globalToolbarContent?.toolbar_right_content?.map((tool, index) => {
        return <GlobalTool key={index} content={tool} iconPack={iconPack} featureToggles={featureToggles} />;
      })}
    </div>
  );

  const primaryNavOptions = (
    <div className="main-nav-links-container">
      {content?.primary_nav?.map((item, index) => {
        // This is required on CS, so this acts as the "default link"
        const navItemWithLink = item.primary_nav_item?.reference.find((el) => el.url || el.link);
        // This will only get rendered if the toggle is on, and if the mega menu content exists
        const megaMenuLink = item.primary_nav_item?.mega_menu?.find(
          (el) => el.column_1_content || el.column_2_content || el.column_3_content || el.column_4_content
        );

        return (
          <ul className="main-nav-links">
            {((navItemWithLink && !megaMenuLink) || !megaMenuEnabled) && (
              <li key={index}>
                <ShLink className="main-nav-link" reference={item.primary_nav_item.reference} featureToggles={[]}>
                  {item.primary_nav_item.title}
                </ShLink>
              </li>
            )}
            {megaMenuLink && megaMenuEnabled && (
              <li key={index}>
                <button
                  className={`main-nav-link unstyled-btn mm-mobile-button ${selectedNavIndexTablet === index ? "active-mm-button" : ""}`}
                  data-testid={`mega-menu-btn-${index}`}
                  onClick={() => handleMegaButtonClick(index)}
                >
                  {item.primary_nav_item.title}
                  <ShIcon iconPack={iconPack} iconName="bold_caret_down" className="mm-mobile-caret-icon" />
                </button>
              </li>
            )}
          </ul>
        );
      })}
    </div>
  );

  const mobileVerticalNav = (
    <>
      <div id="mobile-nav-overlay" className="mobile-nav-overlay">
        {!megaMenuEnabled && <Media at="tablet">{topSectionCloseIcon}</Media>}
        <Media lessThan="tablet">{topSectionCloseIcon}</Media>
        {primaryNavOptions}
        {globalToolbarEnabled ? navActionLinks : null}
      </div>
      <Media lessThan="tablet">
        {selectedNavIndex !== undefined && uiStateContext.megaMenuOpen && (
          <MegaMenu
            content={content?.primary_nav[selectedNavIndex]?.primary_nav_item?.mega_menu}
            iconPack={iconPack}
            featureToggles={featureToggles}
          />
        )}
      </Media>
    </>
  );

  return (
    <>
      <Media lessThan="tablet">{mobileVerticalNav}</Media>
      <Media at="tablet">
        {megaMenuEnabled && !noMegaMenus && (
          <div className="tablet-nav-w-mega-menu">
            {mobileVerticalNav}
            <MegaMenu
              content={content?.primary_nav[selectedNavIndexTablet]?.primary_nav_item?.mega_menu}
              iconPack={iconPack}
              featureToggles={featureToggles}
            />
          </div>
        )}
        {(!megaMenuEnabled || noMegaMenus) && (
          <div id="mobile-nav-overlay" className="mobile-nav-overlay">
            <Flex alignItems="center" flexDirection="row-reverse" width={layout.pageWidth} padding="16px 16px 32px 0px">
              <button className="unstyled-btn close-btn" data-testid="close-btn-top" onClick={() => handleCloseButton()}>
                <ShIcon iconPack={iconPack} className="icon close-icon" iconName="close" />
              </button>
            </Flex>
            {primaryNavOptions}
            {globalToolbarEnabled ? navActionLinks : null}
          </div>
        )}
      </Media>
    </>
  );
};

export default MobileNavOverlay;
